import React, { useEffect, useState } from "react";
import axios from "axios";
import ipconfig from "../../Core/ipconfig";
import {
  Box,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Tab,
  Tabs,
  Typography,
  Chip,
  Divider,
  useTheme,
} from "@mui/material";
import SearchBar from "../../Shared/Widgets/SearchBar";
import PartnersDetail from "./PartnersDetails";
import PartnerReferrals from "./PartnerReferrals";
import PartnerConnectionRequests from "./PartnerConnectionRequests";
import PartnerCommissions from "./PartnerCommissions";
import PartnerStats from "./PartnerStats";

export default function Partners() {
  const theme = useTheme();
  const [searchValue, setSearchValue] = useState("");
  const [tabValue, setTabValue] = useState(0);
  const [allPartners, setAllPartners] = useState<any[]>([]);
  const [filteredPartners, setFilteredPartners] = useState<any[]>([]);
  const [selectedPartner, setSelectedPartner] = useState<any>(null);
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [sortBy, setSortBy] = useState("A-Z");
  const [refreshKey, setRefreshKey] = useState(0);

  const fetchPartners = async () => {
    try {
      const res = await axios.get(
        `${ipconfig}/fetch/partner?sortBy=${sortBy}`,
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
        }
      );

      if (res.status === 200) {
        setAllPartners(res.data.data || []);
        setFilteredPartners(res.data.data || []);
        setSelectedPartner(res.data.data?.[selectedIndex] || null);
      }
    } catch (err) {
      console.error("Fetch partner error", err);
    }
  };

  useEffect(() => {
    let data = [...allPartners];

    if (searchValue) {
      data = allPartners.filter((p) =>
        p?.contactPersonName
          ?.toLowerCase()
          .includes(searchValue.toLowerCase())
      );
    }

    const sortedData = sortPartners(data);
    setFilteredPartners(sortedData);
    setSelectedPartner(sortedData?.[0] || null);
    setSelectedIndex(0);
  }, [searchValue, allPartners, sortBy]);

  const sortPartners = (data: any[]) => {
    if (sortBy === "A-Z") {
      return [...data].sort((a, b) =>
        (a.contactPersonName || "").localeCompare(b.contactPersonName || "")
      );
    }
    if (sortBy === "Z-A") {
      return [...data].sort((a, b) =>
        (b.contactPersonName || "").localeCompare(a.contactPersonName || "")
      );
    }
    return data;
  };

  useEffect(() => {
    fetchPartners();
  }, [sortBy, refreshKey]);

  const refreshData = () => setRefreshKey((p) => p + 1);

  return (
    <Box sx={{ display: "flex", width: "100%", height: "100%", gap: "10px", p: 1 }}>
      {/* Left Side: Search Bar + Partner List */}
      <Box sx={{ width: "320px", display: "flex", flexDirection: "column", gap: "12px", flexShrink: 0 }}>
        <Box sx={{ width: "100%" }}>
          <SearchBar onSearch={setSearchValue} />
        </Box>

        <Box
          sx={{
            backgroundColor: "background.paper",
            borderRadius: "12px",
            border: "1px solid",
            borderColor: "divider",
            height: "100%",
            display: "flex",
            flexDirection: "column",
            overflow: "hidden",
          }}
        >
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              padding: "12px 16px",
              alignItems: "center",
              borderBottom: "1px solid",
              borderColor: "divider",
              gap: "8px"
            }}
          >
            <Typography sx={{ color: "text.primary", fontSize: "15px", fontWeight: 700, whiteSpace: "nowrap" }}>
              Partners ({filteredPartners.length})
            </Typography>
            <FormControl size="small" sx={{ width: "120px" }}>
              <InputLabel id="partner-sort-label">Sort</InputLabel>
              <Select
                labelId="partner-sort-label"
                value={sortBy}
                label="Sort"
                onChange={(e) => setSortBy(e.target.value)}
              >
                <MenuItem value="A-Z">A-Z</MenuItem>
                <MenuItem value="Z-A">Z-A</MenuItem>
              </Select>
            </FormControl>
          </Box>

          <Box
            sx={{
              flexGrow: 1,
              overflowY: "auto",
              padding: "8px",
              display: "flex",
              flexDirection: "column",
              gap: "8px",
              "&::-webkit-scrollbar": {
                width: "5px",
              },
              "&::-webkit-scrollbar-thumb": {
                backgroundColor: "divider",
                borderRadius: "4px",
              },
            }}
          >
            {filteredPartners.map((partner, index) => {
              const isSelected = selectedPartner && selectedPartner._id === partner._id;
              return (
                <Box
                  key={partner._id}
                  onClick={() => {
                    setSelectedPartner(partner);
                    setSelectedIndex(index);
                  }}
                  sx={{
                    padding: "12px",
                    display: "flex",
                    justifyContent: "space-between",
                    cursor: "pointer",
                    backgroundColor: isSelected
                      ? theme.palette.mode === "light" ? "#f0f7ff" : "#1e3a8a33"
                      : "background.paper",
                    border: "1px solid",
                    borderColor: isSelected ? "primary.main" : "divider",
                    borderRadius: "8px",
                    alignItems: "center",
                    "&:hover": {
                      backgroundColor: theme.palette.mode === "light" ? "#f8fafc" : "#1e293b80",
                    },
                    transition: "all 0.2s ease",
                  }}
                >
                  <Box sx={{ minWidth: 0, mr: 1 }}>
                    <Typography sx={{ fontWeight: 600, fontSize: "14px", color: "text.primary", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                      {partner.contactPersonName}
                    </Typography>
                    <Typography variant="caption" sx={{ color: "text.secondary", overflow: "hidden", textOverflow: "ellipsis", display: "block" }}>
                      {partner.emailId}
                    </Typography>
                  </Box>
                  <Chip
                    size="small"
                    label={partner.approvedStatus ? "Approved" : "Pending"}
                    color={partner.approvedStatus ? "success" : "warning"}
                    sx={{ fontWeight: 700, fontSize: "10px", height: "20px" }}
                  />
                </Box>
              );
            })}
          </Box>
        </Box>
      </Box>

      {/* Right Side: Tab panel and details */}
      <Box sx={{ flexGrow: 1, display: "flex", flexDirection: "column", overflow: "hidden" }}>
        {selectedPartner ? (
          <>
            <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: "12px" }}>
              <Box>
                <Typography variant="h6" sx={{ fontWeight: 700, color: "text.primary" }}>
                  {selectedPartner.contactPersonName}
                </Typography>
                <Typography variant="body2" sx={{ color: "text.secondary" }}>
                  {selectedPartner.emailId}
                </Typography>
              </Box>
              <Chip
                label={selectedPartner.approvedStatus ? "Approved Partner" : "Pending Approval"}
                color={selectedPartner.approvedStatus ? "success" : "warning"}
                sx={{ fontWeight: 750 }}
              />
            </Box>

            <Box sx={{ borderBottom: 1, borderColor: "divider", mb: "12px" }}>
              <Tabs value={tabValue} onChange={(_, v) => setTabValue(v)} variant="scrollable">
                <Tab label="Stats" sx={{ fontWeight: 600, textTransform: "none", fontSize: "14px" }} />
                <Tab label="Referrals" sx={{ fontWeight: 600, textTransform: "none", fontSize: "14px" }} />
                <Tab label="Connection Requests" sx={{ fontWeight: 600, textTransform: "none", fontSize: "14px" }} />
                <Tab label="Commissions" sx={{ fontWeight: 600, textTransform: "none", fontSize: "14px" }} />
                <Tab label="Profile" sx={{ fontWeight: 600, textTransform: "none", fontSize: "14px" }} />
              </Tabs>
            </Box>

            <Box
              sx={{
                flexGrow: 1,
                backgroundColor: "background.paper",
                borderRadius: "12px",
                border: "1px solid",
                borderColor: "divider",
                overflow: "auto",
                padding: "20px",
                height: "100%",
              }}
            >
              {tabValue === 0 && (
                <PartnerStats
                  partnerId={selectedPartner._id}
                  onNavigate={(targetTab: number) => setTabValue(targetTab)}
                />
              )}
              {tabValue === 1 && (
                <PartnerReferrals partnerId={selectedPartner._id} />
              )}
              {tabValue === 2 && (
                <PartnerConnectionRequests partnerId={selectedPartner._id} />
              )}
              {tabValue === 3 && (
                <PartnerCommissions partnerId={selectedPartner._id} />
              )}
              {tabValue === 4 && (
                <PartnersDetail
                  data={selectedPartner}
                  onEditSuccess={refreshData}
                />
              )}
            </Box>
          </>
        ) : (
          <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", height: "100%" }}>
            <Typography variant="body1" sx={{ color: "text.secondary", fontWeight: 500 }}>
              No partner selected.
            </Typography>
          </Box>
        )}
      </Box>
    </Box>
  );
}
